import React, { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

interface BulkLeadImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ColumnInfo {
  name: string;
  required: boolean;
  description: string;
}

interface ValidationError {
  field: string;
  message: string;
}

interface ValidationResult {
  rowIndex: number;
  originalData: Record<string, string>;
  resolvedData: Record<string, any>;
  errors: ValidationError[];
  warnings: ValidationError[];
  isValid: boolean;
}

interface TemplateData {
  columns: ColumnInfo[];
  availableStages: string[];
  availableSources: string[];
  availableSegments: Array<{ id: number; name: string; code: string }>;
  availableUsers: { name: string; email: string }[];
  csvTemplate: string;
  instructions: {
    overview: string;
    requiredFields: string;
    nameMatching: string;
    dateFormat: string;
    tagsFormat: string;
    errorHandling: string;
    stageSegmentRule: string;
  };
}

interface ImportResult {
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  creationErrorCount: number;
  created: Array<{
    rowIndex: number;
    leadId: number;
    leadUid: string;
    name: string;
    email: string;
  }>;
  skipped: Array<{
    rowIndex: number;
    errors: ValidationError[];
    originalData: Record<string, string>;
  }>;
  creationErrors: Array<{
    rowIndex: number;
    error: string;
    originalData: Record<string, string>;
  }>;
}

const BulkLeadImportModal: React.FC<BulkLeadImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const [rawData, setRawData] = useState('');
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(true);
  const [showFieldReference, setShowFieldReference] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch template data on mount
  useEffect(() => {
    if (isOpen) {
      fetchTemplate();
      // Reset state when modal opens
      setStep('upload');
      setRawData('');
      setValidationResults([]);
      setImportResult(null);
      setError(null);
    }
  }, [isOpen]);

  const fetchTemplate = async () => {
    try {
      const response = await api.get('/leads/bulk/template');
      if (response.data.success) {
        setTemplateData(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load template data');
    }
  };

  // Parse CSV data
  const parseCSV = (csvText: string): Record<string, string>[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    // Parse header - handle quoted values
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]).map(h => h.replace(/"/g, '').trim());
    const data: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const values = parseCSVLine(line);
      const row: Record<string, string> = {};

      headers.forEach((header, index) => {
        row[header] = (values[index] || '').replace(/"/g, '').trim();
      });

      data.push(row);
    }

    return data;
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setRawData(text);
      setError(null);
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  // Download template CSV
  const downloadTemplate = () => {
    if (!templateData) return;

    const blob = new Blob([templateData.csvTemplate], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Validate the CSV data
  const validateData = async () => {
    setLoading(true);
    setError(null);

    try {
      const parsedData = parseCSV(rawData);
      
      if (parsedData.length === 0) {
        setError('No data found in the CSV. Make sure you have a header row and at least one data row.');
        setLoading(false);
        return;
      }

      const response = await api.post('/leads/bulk/validate', {
        leads: parsedData
      });

      if (response.data.success) {
        setValidationResults(response.data.data.results);
        setStep('preview');
      } else {
        setError(response.data.message || 'Validation failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate data');
    } finally {
      setLoading(false);
    }
  };

  // Import leads (skip errors if any)
  const importLeads = async (skipErrors: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const parsedData = parseCSV(rawData);

      const response = await api.post('/leads/bulk', {
        leads: parsedData,
        skipErrors
      });

      if (response.data.success) {
        setImportResult(response.data.data);
        setStep('result');
      } else {
        // Handle validation errors returned from bulk endpoint
        if (response.data.data?.errors) {
          setError(`${response.data.message}. Please fix the errors and try again.`);
        } else {
          setError(response.data.message || 'Import failed');
        }
      }
    } catch (err: any) {
      const errData = err.response?.data;
      if (errData?.data?.errors) {
        setError(`${errData.message || 'Validation failed'}. Please review errors in the preview.`);
      } else {
        setError(errData?.message || 'Failed to import leads');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle successful completion
  const handleComplete = () => {
    onSuccess();
    onClose();
  };

  // Count valid and invalid rows
  const validCount = validationResults.filter(r => r.isValid).length;
  const errorCount = validationResults.filter(r => !r.isValid).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-0 w-11/12 max-w-6xl shadow-lg rounded-lg bg-white mb-10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <DocumentArrowUpIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Bulk Lead Import
            </h2>
            {step === 'preview' && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                Step 2: Preview
              </span>
            )}
            {step === 'result' && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                Complete
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <XCircleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Guide Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg">
                <button
                  onClick={() => setShowGuide(!showGuide)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-900">Import Guide & Instructions</span>
                  </div>
                  {showGuide ? (
                    <ChevronUpIcon className="h-5 w-5 text-blue-600" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-blue-600" />
                  )}
                </button>
                
                {showGuide && templateData && (
                  <div className="px-4 pb-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <h4 className="font-medium text-gray-900 mb-2">Overview</h4>
                        <p className="text-sm text-gray-600">{templateData.instructions.overview}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <h4 className="font-medium text-gray-900 mb-2">Required Fields</h4>
                        <p className="text-sm text-gray-600">{templateData.instructions.requiredFields}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <h4 className="font-medium text-gray-900 mb-2">Name Matching</h4>
                        <p className="text-sm text-gray-600">{templateData.instructions.nameMatching}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <h4 className="font-medium text-gray-900 mb-2">Date Format</h4>
                        <p className="text-sm text-gray-600">{templateData.instructions.dateFormat}</p>
                      </div>
                    </div>

                    {/* Stage-Segment Rule - Highlighted */}
                    <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-orange-900 mb-1">Stage & Segment Compatibility Rule</h4>
                          <p className="text-sm text-orange-800">{templateData.instructions.stageSegmentRule}</p>
                        </div>
                      </div>
                    </div>

                    {/* Available Options */}
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h4 className="font-medium text-gray-900 mb-3">Available Options (use these names in your CSV)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-700 mb-1">Stages:</p>
                          <div className="flex flex-wrap gap-1">
                            {templateData.availableStages.map(stage => (
                              <span key={stage} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                {stage}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 mb-1">Sources:</p>
                          <div className="flex flex-wrap gap-1">
                            {templateData.availableSources.map(source => (
                              <span key={source} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                {source}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 mb-1">Customer Segments:</p>
                          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                            {templateData.availableSegments && templateData.availableSegments.length > 0 ? (
                              templateData.availableSegments.map(segment => (
                                <span key={segment.id} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                  {segment.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500 text-xs italic">No segments configured</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="font-medium text-gray-700 mb-1">Users (Lead Owner / Assigned To):</p>
                        <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                          {templateData.availableUsers.slice(0, 10).map(user => (
                            <span key={user.email} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                              {user.name || user.email}
                            </span>
                          ))}
                          {templateData.availableUsers.length > 10 && (
                            <span className="px-2 py-0.5 text-gray-500 text-xs">
                              +{templateData.availableUsers.length - 10} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Field Reference */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg">
                <button
                  onClick={() => setShowFieldReference(!showFieldReference)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <span className="font-medium text-gray-900">Field Reference</span>
                  </div>
                  {showFieldReference ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                  )}
                </button>
                
                {showFieldReference && templateData && (
                  <div className="px-4 pb-4">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {templateData.columns.map(col => (
                            <tr key={col.name} className={col.required ? 'bg-yellow-50' : ''}>
                              <td className="px-3 py-2 text-sm font-mono text-gray-900">{col.name}</td>
                              <td className="px-3 py-2 text-sm">
                                {col.required ? (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs">Required</span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Optional</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-600">{col.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <DocumentArrowDownIcon className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <p className="font-medium text-green-900">Download Example CSV Template</p>
                    <p className="text-sm text-green-700">Get a pre-filled template with sample data to see the expected format</p>
                  </div>
                </div>
                <button
                  onClick={downloadTemplate}
                  disabled={!templateData}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  Download Template
                </button>
              </div>

              {/* File Upload */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".csv"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                    Upload CSV File
                  </button>
                  <span className="text-sm text-gray-500">or paste CSV data below</span>
                </div>

                <textarea
                  value={rawData}
                  onChange={(e) => setRawData(e.target.value)}
                  placeholder="Paste your CSV data here..."
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none"
                />

                {rawData && (
                  <div className="text-sm text-gray-600">
                    {parseCSV(rawData).length} rows detected
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <span className="font-medium text-gray-900">{validCount} Valid</span>
                  </div>
                  {errorCount > 0 && (
                    <div className="flex items-center">
                      <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                      <span className="font-medium text-gray-900">{errorCount} Errors</span>
                    </div>
                  )}
                  <div className="text-sm text-gray-500">
                    Total: {validationResults.length} rows
                  </div>
                </div>
                <button
                  onClick={() => setStep('upload')}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Back to Upload
                </button>
              </div>

              {/* Error Summary */}
              {errorCount > 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">
                        {errorCount} row{errorCount > 1 ? 's have' : ' has'} errors
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        You can either fix the errors and re-upload, or skip the error rows and create only the valid leads.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Owner</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segments</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {validationResults.map((result) => (
                        <tr 
                          key={result.rowIndex} 
                          className={result.isValid ? '' : 'bg-red-50'}
                        >
                          <td className="px-3 py-2 text-sm text-gray-900">{result.rowIndex + 2}</td>
                          <td className="px-3 py-2">
                            {result.isValid ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-600" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {result.resolvedData.firstName} {result.resolvedData.lastName}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">{result.resolvedData.email}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {result.resolvedData.leadOwnerName || result.originalData.leadOwner || '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {result.resolvedData.stageName || result.originalData.stage || '-'}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {result.resolvedData.segmentNames ? (
                              <div className="flex flex-wrap gap-1">
                                {result.resolvedData.segmentNames.split(', ').map((seg: string, i: number) => (
                                  <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                                    {seg}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {result.errors.length > 0 && (
                              <div className="space-y-1">
                                {result.errors.map((err, i) => (
                                  <div key={i} className="text-xs text-red-600">
                                    <span className="font-medium">{err.field}:</span> {err.message}
                                  </div>
                                ))}
                              </div>
                            )}
                            {result.warnings.length > 0 && (
                              <div className="space-y-1">
                                {result.warnings.map((warn, i) => (
                                  <div key={i} className="text-xs text-yellow-600">
                                    <span className="font-medium">{warn.field}:</span> {warn.message}
                                  </div>
                                ))}
                              </div>
                            )}
                            {result.isValid && result.errors.length === 0 && result.warnings.length === 0 && (
                              <span className="text-xs text-green-600">Ready to import</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 'result' && importResult && (
            <div className="space-y-6">
              {/* Success Summary */}
              <div className="p-6 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-green-900 mb-2">
                  Import Complete!
                </h3>
                <p className="text-green-700">
                  Successfully created {importResult.createdCount} lead{importResult.createdCount !== 1 ? 's' : ''}
                  {importResult.skippedCount > 0 && ` (${importResult.skippedCount} skipped)`}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-green-100 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-800">{importResult.createdCount}</div>
                  <div className="text-sm text-green-700">Created</div>
                </div>
                <div className="p-4 bg-yellow-100 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-800">{importResult.skippedCount}</div>
                  <div className="text-sm text-yellow-700">Skipped (Errors)</div>
                </div>
                <div className="p-4 bg-red-100 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-800">{importResult.creationErrorCount}</div>
                  <div className="text-sm text-red-700">Failed</div>
                </div>
              </div>

              {/* Created Leads */}
              {importResult.created.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Created Leads</h4>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {importResult.created.map((lead) => (
                          <tr key={lead.leadId}>
                            <td className="px-3 py-2 text-sm font-mono text-blue-600">{lead.leadUid}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{lead.name}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{lead.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Skipped/Failed */}
              {(importResult.skipped.length > 0 || importResult.creationErrors.length > 0) && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Skipped/Failed Rows</h4>
                  <div className="border border-red-200 rounded-lg max-h-48 overflow-auto bg-red-50">
                    <div className="p-4 space-y-2 text-sm">
                      {importResult.skipped.map((item, i) => (
                        <div key={i} className="flex items-start">
                          <span className="font-medium text-red-800 mr-2">Row {item.rowIndex + 2}:</span>
                          <span className="text-red-700">
                            {item.errors.map(e => `${e.field}: ${e.message}`).join('; ')}
                          </span>
                        </div>
                      ))}
                      {importResult.creationErrors.map((item, i) => (
                        <div key={`err-${i}`} className="flex items-start">
                          <span className="font-medium text-red-800 mr-2">Row {item.rowIndex + 2}:</span>
                          <span className="text-red-700">{item.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {step === 'result' ? 'Close' : 'Cancel'}
          </button>

          <div className="flex space-x-3">
            {step === 'upload' && (
              <button
                onClick={validateData}
                disabled={!rawData.trim() || loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  'Validate & Preview'
                )}
              </button>
            )}

            {step === 'preview' && (
              <>
                {errorCount > 0 && validCount > 0 && (
                  <button
                    onClick={() => importLeads(true)}
                    disabled={loading}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors flex items-center"
                  >
                    {loading ? (
                      <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : null}
                    Skip Errors & Create {validCount}
                  </button>
                )}
                {validCount > 0 && errorCount === 0 && (
                  <button
                    onClick={() => importLeads(false)}
                    disabled={loading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
                  >
                    {loading ? (
                      <>
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      `Import ${validCount} Lead${validCount !== 1 ? 's' : ''}`
                    )}
                  </button>
                )}
                {validCount === 0 && (
                  <button
                    onClick={() => setStep('upload')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Go Back & Fix Errors
                  </button>
                )}
              </>
            )}

            {step === 'result' && (
              <button
                onClick={handleComplete}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Done - View Leads
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLeadImportModal;
